import { useEffect, useState, useCallback } from "react";
import { Loader2, Download, FileText, Plus, CheckCircle2, Wallet, Pencil } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPES = [
  { key: "doctor_comp", l: "t_doctor_comp" },
  { key: "internal_lab", l: "t_internal_lab" },
  { key: "external_lab", l: "t_external_lab" },
];
const CURRENCIES = ["EUR", "USD", "TRY", "GBP"];
const METHODS = [
  { v: "cash", l: "m_cash" }, { v: "bank_transfer", l: "m_bank_transfer" },
  { v: "credit_card", l: "m_credit_card" },
];
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const eur = (n) => `€${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_STYLE = { accrued: "bg-slate-100 text-slate-600", approved: "bg-blue-100 text-blue-700", paid: "bg-emerald-100 text-emerald-700" };

async function download(url, filename) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const href = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = href; a.download = filename; a.click();
    URL.revokeObjectURL(href);
  } catch { toast.error("Export failed"); }
}

export default function Payables() {
  const { user } = useAuth();
  const t = useT();
  const canPay = ["owner", "operations_manager", "general_accountant"].includes(user?.role);
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [gen, setGen] = useState({ type: "doctor_comp", beneficiary_id: "", period_start: firstOfMonth(), period_end: new Date().toISOString().slice(0, 10) });
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const q = filterType === "all" ? "" : `?type=${filterType}`;
    api.get(`/accounting/statements${q}`).then((r) => setStatements(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [filterType]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get(`/accounting/beneficiaries?type=${gen.type}`).then((r) => setBeneficiaries(r.data)).catch(() => setBeneficiaries([]));
  }, [gen.type]);

  const generate = async () => {
    if (!gen.beneficiary_id) { toast.error(t("payables.selectBeneficiary")); return; }
    setBusy(true);
    try {
      const r = await api.post("/accounting/statements/generate", gen);
      toast.success(`${t("payables.statementGenerated")} · ${eur(r.data.earned_total_eur)}`);
      setDetail(r.data); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]">
      <PageHeader title={t("payables.title")}
        subtitle={t("payables.subtitle")} />

      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6" data-testid="gen-panel">
        <h3 className="font-heading text-lg text-slate-900 mb-3 flex items-center gap-2"><Plus className="h-5 w-5" /> {t("payables.generateStatement")}</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="text-xs text-slate-500">{t("payables.type")}</label>
            <Select value={gen.type} onValueChange={(v) => setGen((g) => ({ ...g, type: v, beneficiary_id: "" }))}>
              <SelectTrigger className="w-52" data-testid="gen-type"><SelectValue /></SelectTrigger>
              <SelectContent>{TYPES.map((ty) => <SelectItem key={ty.key} value={ty.key}>{t(`payables.${ty.l}`)}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><label className="text-xs text-slate-500">{t("payables.beneficiary")}</label>
            <Select value={gen.beneficiary_id} onValueChange={(v) => setGen((g) => ({ ...g, beneficiary_id: v }))}>
              <SelectTrigger className="w-56" data-testid="gen-beneficiary"><SelectValue placeholder={t("payables.beneficiary")} /></SelectTrigger>
              <SelectContent>{beneficiaries.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                {beneficiaries.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">{t("payables.noneAvailable")}</div>}</SelectContent>
            </Select></div>
          <div><label className="text-xs text-slate-500">{t("payables.from")}</label>
            <Input type="date" value={gen.period_start} onChange={(e) => setGen((g) => ({ ...g, period_start: e.target.value }))} className="h-9 w-40" data-testid="gen-start" /></div>
          <div><label className="text-xs text-slate-500">{t("payables.to")}</label>
            <Input type="date" value={gen.period_end} onChange={(e) => setGen((g) => ({ ...g, period_end: e.target.value }))} className="h-9 w-40" data-testid="gen-end" /></div>
          <Button className="bg-navy hover:bg-navy-soft text-white h-9" disabled={busy} onClick={generate} data-testid="gen-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("payables.generate")}</Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-52 h-9" data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{t("payables.allTypes")}</SelectItem>
            {TYPES.map((ty) => <SelectItem key={ty.key} value={ty.key}>{t(`payables.${ty.l}`)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div> : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="statements-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr><th className="px-4 py-2 font-medium">{t("payables.beneficiary")}</th><th className="px-4 py-2 font-medium">{t("payables.type")}</th>
                <th className="px-4 py-2 font-medium">{t("payables.period")}</th><th className="px-4 py-2 font-medium">{t("payables.earned")}</th>
                <th className="px-4 py-2 font-medium">{t("payables.net")}</th><th className="px-4 py-2 font-medium">{t("payables.paid")}</th>
                <th className="px-4 py-2 font-medium">{t("payables.outstanding")}</th><th className="px-4 py-2 font-medium">{t("payables.status")}</th><th></th></tr>
            </thead>
            <tbody>
              {statements.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">{t("payables.noStatements")}</td></tr>}
              {statements.map((s) => (
                <tr key={s.id} className="border-t border-slate-50 hover:bg-slate-50 cursor-pointer" data-testid={`statement-row-${s.id}`} onClick={() => setDetail(s)}>
                  <td className="px-4 py-2 font-medium text-slate-800">{s.beneficiary_name}</td>
                  <td className="px-4 py-2 text-slate-500">{s.type_label}</td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{s.period_start} → {s.period_end}</td>
                  <td className="px-4 py-2 tabular-nums">{eur(s.earned_total_eur)}</td>
                  <td className="px-4 py-2 tabular-nums">{eur(s.net_payable_eur)}</td>
                  <td className="px-4 py-2 tabular-nums">{eur(s.paid_total_eur)}</td>
                  <td className="px-4 py-2 tabular-nums font-medium">{eur(s.outstanding_eur)}</td>
                  <td className="px-4 py-2"><Badge className={`${STATUS_STYLE[s.status]} font-normal`}>{t(`payables.s_${s.status}`, s.status)}</Badge></td>
                  <td className="px-4 py-2 text-slate-400 text-xs">{t("payables.view")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && <StatementDialog s={detail} canPay={canPay} t={t} onClose={() => setDetail(null)} onChanged={(v) => { setDetail(v); load(); }} />}
    </div>
  );
}

function StatementDialog({ s, canPay, t, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [adj, setAdj] = useState({ delta_eur: "", reason: "" });
  const [pay, setPay] = useState({ amount: "", currency: "EUR", method: "cash", reference: "" });

  const call = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); toast.success(ok); onChanged(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{s.beneficiary_name}
            <Badge className={`${STATUS_STYLE[s.status]} font-normal`}>{t(`payables.s_${s.status}`, s.status)}</Badge></DialogTitle>
          <DialogDescription>{s.type_label} statement · {s.period_start} → {s.period_end}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">{s.type_label} · {s.period_start} → {s.period_end}</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-2">
          {[[t("payables.earned"), s.earned_total_eur], [t("payables.adjustments"), s.adjustments_total_eur], [t("payables.paid"), s.paid_total_eur], [t("payables.outstanding"), s.outstanding_eur]].map(([l, v]) => (
            <div key={l} className="bg-slate-50 rounded-lg p-2 text-center"><p className="text-[11px] text-slate-500">{l}</p><p className="font-medium tabular-nums">{eur(v)}</p></div>
          ))}
        </div>

        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-xs" data-testid="statement-lines">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr><th className="px-2 py-1">{t("payables.from")}</th><th className="px-2 py-1">{t("accounting.patient")}</th><th className="px-2 py-1">{t("accounting.item")}</th><th className="px-2 py-1">{t("accounting.qty")}</th><th className="px-2 py-1">{t("accounting.rate")}</th><th className="px-2 py-1">{t("accounting.amount")}</th></tr></thead>
            <tbody>
              {s.lines.length === 0 && <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">{t("payables.noVerifiedWork")}</td></tr>}
              {s.lines.map((e, i) => (
                <tr key={i} className="border-t border-slate-50"><td className="px-2 py-1">{(e.date || "").slice(0, 10)}</td>
                  <td className="px-2 py-1">{e.patient_name || "—"}</td><td className="px-2 py-1">{e.item_label}</td>
                  <td className="px-2 py-1">{e.quantity}</td><td className="px-2 py-1">{eur(e.rate)}</td><td className="px-2 py-1">{eur(e.amount_eur)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>

        {s.adjustments.length > 0 && (
          <div className="text-xs text-slate-600" data-testid="statement-adjustments">
            <p className="font-medium mb-1">{t("payables.adjustmentTrail")}</p>
            {s.adjustments.map((a) => (
              <div key={a.id} className="flex justify-between border-b border-slate-50 py-1">
                <span>{eur(a.delta_eur)} · {a.reason}</span>
                <span className="text-slate-400">{a.by_name} · {eur(a.previous_value_eur)}→{eur(a.new_value_eur)}</span>
              </div>
            ))}
          </div>
        )}

        {s.payments.length > 0 && (
          <div className="text-xs text-slate-600">
            <p className="font-medium mb-1">{t("payables.payments")}</p>
            {s.payments.map((p) => (
              <div key={p.id} className="flex justify-between border-b border-slate-50 py-1">
                <span>{p.amount} {p.currency} ({eur(p.eur_amount)}) · {p.method}{p.cash_shift_id ? " · drawer-linked" : ""}</span>
                <span className="text-slate-400">{p.by_name}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
          <Button variant="outline" size="sm" data-testid="stmt-export-csv" onClick={() => download(`/accounting/statements/${s.id}/export?format=csv`, `statement_${s.id}.csv`)}><Download className="h-4 w-4 mr-1" /> CSV</Button>
          <Button variant="outline" size="sm" data-testid="stmt-export-pdf" onClick={() => download(`/accounting/statements/${s.id}/export?format=pdf`, `statement_${s.id}.pdf`)}><FileText className="h-4 w-4 mr-1" /> PDF</Button>
          {canPay && s.status === "accrued" && (
            <Button size="sm" className="bg-blue-600 text-white" disabled={busy} data-testid="stmt-approve"
              onClick={() => call(() => api.post(`/accounting/statements/${s.id}/approve`), t("payables.approved"))}><CheckCircle2 className="h-4 w-4 mr-1" /> {t("payables.approve")}</Button>
          )}
        </div>

        {canPay && s.status !== "paid" && (
          <div className="grid sm:grid-cols-2 gap-4 pt-2">
            <div className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> {t("payables.adjustment")}</p>
              <div className="flex gap-2 items-end">
                <Input type="number" step="0.01" placeholder="±EUR" value={adj.delta_eur} className="h-8 w-24" data-testid="adj-amount"
                  onChange={(e) => setAdj((a) => ({ ...a, delta_eur: e.target.value }))} />
                <Input placeholder={t("common.reason")} value={adj.reason} className="h-8 flex-1" data-testid="adj-reason"
                  onChange={(e) => setAdj((a) => ({ ...a, reason: e.target.value }))} />
                <Button size="sm" className="h-8" disabled={busy} data-testid="adj-submit"
                  onClick={() => call(() => api.post(`/accounting/statements/${s.id}/adjust`, { delta_eur: Number(adj.delta_eur), reason: adj.reason }), t("payables.adjusted"))}>{t("payables.applyAdj")}</Button>
              </div>
            </div>
            {s.status === "approved" && (
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1"><Wallet className="h-3.5 w-3.5" /> {t("payables.recordPayment")}</p>
                <div className="flex flex-wrap gap-2 items-end">
                  <Input type="number" step="0.01" placeholder="amount" value={pay.amount} className="h-8 w-24" data-testid="pay-amount"
                    onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} />
                  <Select value={pay.currency} onValueChange={(v) => setPay((p) => ({ ...p, currency: v }))}>
                    <SelectTrigger className="h-8 w-20" data-testid="pay-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={pay.method} onValueChange={(v) => setPay((p) => ({ ...p, method: v }))}>
                    <SelectTrigger className="h-8 w-44" data-testid="pay-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{t(`payables.${m.l}`)}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" className="h-8 bg-emerald-600 text-white" disabled={busy} data-testid="pay-submit"
                    onClick={() => call(() => api.post(`/accounting/statements/${s.id}/pay`, { amount: Number(pay.amount), currency: pay.currency, method: pay.method, reference: pay.reference || null }), t("payables.paymentRecorded"))}>{t("payables.pay")}</Button>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
